declare global {
  interface Window {
    ym?: (...args: unknown[]) => void;
    __wgpMetrikaId?: string;
  }
}

export function getMetrikaCounterId() {
  return typeof window === 'undefined' ? undefined : window.__wgpMetrikaId;
}
const LEAD_FORM_GOAL_PREFIX = 'site-form';

function normalizeGoalPart(value: string) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-|-$/g, '');
}

export function reachMetrikaGoal(goalId: string, params?: Record<string, string>) {
  const YM_COUNTER_ID = getMetrikaCounterId();
  if (typeof window === 'undefined' || !YM_COUNTER_ID || typeof window.ym !== 'function') {
    return false;
  }

  const normalizedGoalId = normalizeGoalPart(goalId);
  if (!normalizedGoalId) {
    return false;
  }

  try {
    window.ym(YM_COUNTER_ID, 'reachGoal', normalizedGoalId, params);
    return true;
  } catch {
    return false;
  }
}

export function getLeadFormGoalId(formType: string) {
  const normalizedFormType = normalizeGoalPart(formType);
  if (!normalizedFormType) {
    return null;
  }

  return `${LEAD_FORM_GOAL_PREFIX}-${normalizedFormType}`;
}

export function trackLeadFormGoal(formType: string) {
  const goalId = getLeadFormGoalId(formType);
  if (!goalId) {
    return false;
  }

  return reachMetrikaGoal(goalId);
}

export type LeadFormStage = 'open' | 'start' | 'attempt' | 'error' | 'success';

// Never send field values, error messages, submission IDs or CRM IDs to analytics.
export function trackLeadFormStage(formType: string, stage: LeadFormStage, reason?: string) {
  return reachMetrikaGoal(`lead-form-${stage}`, {
    form_type: normalizeGoalPart(formType),
    ...(reason ? { reason: normalizeGoalPart(reason) } : {}),
  });
}

// Причина ошибки сервера для Метрики: HTTP-статус и короткий код ответа (CAPTCHA_ERROR и т. п.).
export function serverErrorReason(status: number, code?: unknown) {
  const safeCode = typeof code === 'string' && /^[A-Z0-9_]{2,40}$/.test(code) ? `-${code}` : '';
  return `server-${status || 0}${safeCode}`;
}

// TypeError у fetch — сеть или разрыв соединения; остальное — сбой в браузере до отправки.
export function clientErrorReason(cause: unknown) {
  return cause instanceof TypeError ? 'network' : 'client';
}

export function trackLeadFormSuccess(formType: string, submissionId: string) {
  if (typeof window === 'undefined' || !submissionId) return false;
  const key = `lead-thank-you-goal:${submissionId}`;
  try { window.sessionStorage.setItem(`lead-confirmed:${submissionId}`, formType); } catch { /* Optional analytics receipt. */ }
  try {
    if (window.sessionStorage.getItem(key) === '1') return true;
  } catch { /* Analytics must never prevent a successful submission. */ }
  const legacySent = trackLeadFormGoal(formType);
  const sent = trackLeadFormStage(formType, 'success');
  if (legacySent && sent) {
    try { window.sessionStorage.setItem(key, '1'); } catch { /* Storage may be unavailable. */ }
  }
  return legacySent && sent;
}

// Заказ из корзины принят сервером: цель Метрики «Заказ из корзины» и часть главной «Заявка (все)».
// Один раз на номер заказа; номер заказа и состав в Метрику не уходят.
export function trackOrderSuccess(orderId: string | number) {
  const id = String(orderId ?? '').trim();
  if (typeof window === 'undefined' || !id) return false;
  const key = `order-success-goal:${id}`;
  try {
    if (window.sessionStorage.getItem(key) === '1') return true;
  } catch { /* Storage may be unavailable. */ }
  const sent = reachMetrikaGoal('order-success');
  if (sent) {
    try { window.sessionStorage.setItem(key, '1'); } catch { /* Storage may be unavailable. */ }
  }
  return sent;
}
